import * as React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { BudgetForm } from '@/components/budget/BudgetForm';
import {
  useUnassignedSpending,
  useAssignUnassignedSpending,
  useDiscardUnassignedSpending,
} from '@/hooks/useUnassignedSpending';
import { useBudgets, useCreateBudget } from '@/hooks/useBudgets';
import { usePendingInvitations } from '@/hooks/useShares';
import { ShareService } from '@/services/ShareService';
import { cn, formatCurrency, formatDate } from '@/lib/utils';
import type { Budget, CreateBudgetRequest, ShareInvitation, UnassignedSpending, UpdateBudgetRequest } from '@/types';

const DISMISSED_KEY = 'dismissedUnassignedSpending';
const OPEN_EVENT = 'unassigned-spending:open';

export function openUnassignedSpendingPrompt() {
  try {
    sessionStorage.removeItem(DISMISSED_KEY);
  } catch {
    // ignore
  }
  window.dispatchEvent(new Event(OPEN_EVENT));
}

function readDismissed(): boolean {
  try {
    return sessionStorage.getItem(DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

function persistDismissed() {
  try {
    sessionStorage.setItem(DISMISSED_KEY, '1');
  } catch {
    // ignore
  }
}

function clearDismissed() {
  try {
    sessionStorage.removeItem(DISMISSED_KEY);
  } catch {
    // ignore
  }
}

function covers(budget: Pick<Budget, 'startDate' | 'endDate'>, date: string): boolean {
  return date >= budget.startDate && date <= budget.endDate;
}

function invitationCovers(invitation: ShareInvitation, date: string): boolean {
  if (!invitation.budgetStartDate || !invitation.budgetEndDate) return false;
  return date >= invitation.budgetStartDate && date <= invitation.budgetEndDate;
}

function collectEditableBudgets(roots: Budget[]): Budget[] {
  const result: Budget[] = [];
  const walk = (budget: Budget) => {
    const editable = budget.canEdit !== false;
    if (editable) result.push(budget);
    for (const child of budget.children ?? []) walk(child);
  };
  for (const root of roots) walk(root);
  return result;
}

type Destination =
  | { kind: 'budget'; budget: Budget }
  | { kind: 'invitation'; invitation: ShareInvitation };

/**
 * Prompt when Shortcuts (or other API-key) captures landed with no covering budget.
 * Supports partial selection, subset assign by date fit, accept-invite, and create budget.
 *
 * Auto-open is deferred while share invitations are pending so the user can accept
 * a shared budget first, then place spending into it.
 */
export function UnassignedSpendingPromptDialog({
  forceOpen = false,
  holdOff = false,
  onOpenChange,
}: {
  forceOpen?: boolean;
  /** When true (e.g. invitation prompt is open), do not auto-open this dialog. */
  holdOff?: boolean;
  onOpenChange?: (open: boolean) => void;
} = {}) {
  const { data, isLoading } = useUnassignedSpending();
  const { data: budgets = [] } = useBudgets();
  const { pendingInvitations } = usePendingInvitations();
  const assignMutation = useAssignUnassignedSpending();
  const discardMutation = useDiscardUnassignedSpending();
  const createBudget = useCreateBudget();

  const items = data?.items ?? [];
  const [dismissed, setDismissed] = React.useState(readDismissed);
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new Set());
  const [destination, setDestination] = React.useState<Destination | null>(null);
  const [creating, setCreating] = React.useState(false);

  React.useEffect(() => {
    const onOpen = () => {
      clearDismissed();
      setDismissed(false);
    };
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, []);

  React.useEffect(() => {
    if (items.length > 0 && !dismissed) {
      setSelectedIds((prev) => (prev.size === 0 ? new Set(items.map((item) => item.id)) : prev));
    }
  }, [items, dismissed]);

  const autoOpen = items.length > 0 && !dismissed && !holdOff;
  const open = forceOpen ? items.length > 0 : autoOpen;

  React.useEffect(() => {
    onOpenChange?.(open);
  }, [open, onOpenChange]);

  const selectedItems = items.filter((item) => selectedIds.has(item.id));
  const editableBudgets = React.useMemo(() => collectEditableBudgets(budgets), [budgets]);

  const fittingCount = React.useMemo(() => {
    if (!destination || selectedItems.length === 0) return 0;
    if (destination.kind === 'budget') {
      return selectedItems.filter((item) => covers(destination.budget, item.occurredDate)).length;
    }
    return selectedItems.filter((item) =>
      invitationCovers(destination.invitation, item.occurredDate)
    ).length;
  }, [destination, selectedItems]);

  const matchingInvitations = React.useMemo(() => {
    if (selectedItems.length === 0) return pendingInvitations;
    return pendingInvitations.filter((invitation) =>
      selectedItems.some((item) => invitationCovers(invitation, item.occurredDate))
    );
  }, [pendingInvitations, selectedItems]);

  const createDateDefaults = React.useMemo(() => {
    if (selectedItems.length === 0) return { startDate: undefined, endDate: undefined };
    const dates = selectedItems.map((item) => item.occurredDate).sort();
    return { startDate: dates[0], endDate: dates[dates.length - 1] };
  }, [selectedItems]);

  const dismiss = () => {
    persistDismissed();
    setDismissed(true);
    setCreating(false);
    onOpenChange?.(false);
  };

  const toggleItem = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const itemFitLabel = (item: UnassignedSpending): { text: string; tone: 'fit' | 'out' | 'none' } => {
    if (!destination) return { text: '', tone: 'none' };
    const fits =
      destination.kind === 'budget'
        ? covers(destination.budget, item.occurredDate)
        : invitationCovers(destination.invitation, item.occurredDate);
    return fits
      ? { text: 'Fits selected budget', tone: 'fit' }
      : { text: 'Outside selected period — will stay unassigned', tone: 'out' };
  };

  const handleAssign = async () => {
    if (!destination || selectedItems.length === 0) return;
    const ids = selectedItems.map((item) => item.id);
    if (destination.kind === 'budget') {
      await assignMutation.mutateAsync({ ids, budgetId: destination.budget.id });
    } else {
      await assignMutation.mutateAsync({
        ids,
        budgetId: destination.invitation.budgetId,
        acceptShareId: destination.invitation.shareId,
      });
    }
    setCreating(false);
  };

  const handleDiscard = async () => {
    if (selectedItems.length === 0) return;
    await discardMutation.mutateAsync(selectedItems.map((item) => item.id));
  };

  const handleCreateBudget = async (data: CreateBudgetRequest | UpdateBudgetRequest) => {
    const created = await createBudget.mutateAsync(data as CreateBudgetRequest);
    const ids = selectedItems.map((item) => item.id);
    if (ids.length > 0) {
      await assignMutation.mutateAsync({ ids, budgetId: created.id });
    }
    setCreating(false);
  };

  if (isLoading && items.length === 0 && !forceOpen) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) dismiss();
      }}
    >
      <DialogContent
        className="max-h-[85vh] overflow-y-auto sm:max-w-lg"
        data-testid="unassigned-spending-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {items.length === 1 ? 'Unassigned spending' : `${items.length} unassigned purchases`}
          </DialogTitle>
          <DialogDescription>
            Captures from Shortcuts with no covering budget. Assign them now or come back later.
          </DialogDescription>
        </DialogHeader>

        {creating ? (
          <div className="space-y-3">
            <BudgetForm
              parentBudgets={budgets}
              initialStartDate={createDateDefaults.startDate}
              initialEndDate={createDateDefaults.endDate}
              onSubmit={handleCreateBudget}
              onCancel={() => setCreating(false)}
              isLoading={createBudget.isPending || assignMutation.isPending}
            />
          </div>
        ) : (
          <>
            <section className="space-y-2">
              <h3 className="text-sm font-medium text-foreground">Purchases</h3>
              <ul className="space-y-2">
                {items.map((item) => {
                  const fit = selectedIds.has(item.id) ? itemFitLabel(item) : { text: '', tone: 'none' as const };
                  return (
                    <li key={item.id}>
                      <label
                        className={cn(
                          'flex cursor-pointer items-start gap-3 rounded-md border p-3',
                          selectedIds.has(item.id) && 'border-primary/40 bg-primary/5'
                        )}
                      >
                        <input
                          type="checkbox"
                          className="mt-1"
                          checked={selectedIds.has(item.id)}
                          onChange={() => toggleItem(item.id)}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <p className="font-medium text-foreground truncate">{item.merchant}</p>
                            <p className="shrink-0 text-sm font-medium">
                              {formatCurrency(item.amount, item.currency ?? 'EUR')}
                            </p>
                          </div>
                          <p className="text-xs text-muted-foreground">{formatDate(item.occurredDate)}</p>
                          {fit.tone !== 'none' && (
                            <p
                              className={cn(
                                'mt-1 text-xs',
                                fit.tone === 'fit' ? 'text-success' : 'text-warning'
                              )}
                            >
                              {fit.text}
                            </p>
                          )}
                        </div>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </section>

            <section className="space-y-2">
              <h3 className="text-sm font-medium text-foreground">Send to</h3>
              <div className="space-y-2">
                {editableBudgets.map((budget) => {
                  const selected =
                    destination?.kind === 'budget' && destination.budget.id === budget.id;
                  return (
                    <button
                      key={budget.id}
                      type="button"
                      onClick={() => setDestination({ kind: 'budget', budget })}
                      className={cn(
                        'flex w-full flex-col items-start rounded-md border px-3 py-2 text-left text-sm',
                        selected ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
                      )}
                    >
                      <span className="font-medium">{budget.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {formatDate(budget.startDate)} – {formatDate(budget.endDate)}
                        {budget.owned === false ? ' · Shared' : ''}
                      </span>
                    </button>
                  );
                })}

                {matchingInvitations.map((invitation) => {
                  const selected =
                    destination?.kind === 'invitation' &&
                    destination.invitation.shareId === invitation.shareId;
                  return (
                    <button
                      key={invitation.shareId}
                      type="button"
                      onClick={() => setDestination({ kind: 'invitation', invitation })}
                      className={cn(
                        'flex w-full flex-col items-start rounded-md border px-3 py-2 text-left text-sm',
                        selected ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
                      )}
                    >
                      <span className="font-medium">{invitation.budgetName}</span>
                      <span className="text-xs text-muted-foreground">
                        Invitation from {invitation.ownerEmail} ·{' '}
                        {ShareService.getRoleName(invitation.role)}
                        {invitation.budgetStartDate && invitation.budgetEndDate
                          ? ` · ${formatDate(invitation.budgetStartDate)} – ${formatDate(invitation.budgetEndDate)}`
                          : ''}
                      </span>
                      <span className="mt-1 text-xs font-medium text-primary">Accept & assign</span>
                    </button>
                  );
                })}

                <button
                  type="button"
                  className="text-sm font-medium text-primary hover:underline"
                  onClick={() => setCreating(true)}
                >
                  + Create new budget covering selected dates
                </button>
              </div>
            </section>

            {destination && selectedItems.length > 0 && (
              <p className="text-xs text-muted-foreground">
                {fittingCount} will be assigned · {selectedItems.length - fittingCount} stays
                unassigned
              </p>
            )}

            <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between">
              <Button variant="ghost" onClick={dismiss}>
                Later
              </Button>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  disabled={selectedItems.length === 0 || discardMutation.isPending}
                  loading={discardMutation.isPending}
                  onClick={handleDiscard}
                >
                  Discard selected
                </Button>
                <Button
                  disabled={
                    !destination ||
                    selectedItems.length === 0 ||
                    fittingCount === 0 ||
                    assignMutation.isPending
                  }
                  loading={assignMutation.isPending}
                  onClick={handleAssign}
                >
                  Assign {fittingCount > 0 ? `${fittingCount} fitting` : 'selected'}
                </Button>
              </div>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
